import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BeakerIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(`${href}/`);

  const desktopLinkClasses = (href) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center ${
      isActive(href)
        ? 'text-primary-700 bg-primary-50'
        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
    }`;

  const mobileLinkClasses = (href) =>
    `flex items-center px-3 py-3 rounded-md text-base font-medium transition-colors duration-150 ${
      isActive(href)
        ? 'text-primary-700 bg-primary-50'
        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
    }`;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and desktop navigation */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-primary-500 rounded-lg flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm">AIQ</span>
              </div>
              <span className="font-semibold text-gray-900 text-lg hidden sm:block">AllocIQ</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex ml-10 space-x-1">
              <Link
                to="/chat"
                aria-current={isActive('/chat') ? 'page' : undefined}
                className={desktopLinkClasses('/chat')}
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                Chat
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  aria-current={isActive('/admin') ? 'page' : undefined}
                  className={desktopLinkClasses('/admin')}
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                  Admin
                </Link>
              )}
              <Link
                to="/experimental"
                aria-current={isActive('/experimental') ? 'page' : undefined}
                className={desktopLinkClasses('/experimental')}
              >
                <BeakerIcon className="h-4 w-4 mr-2" />
                Clinician Worklist
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wide">Beta</span>
              </Link>
              <Link
                to="/survey"
                aria-current={isActive('/survey') ? 'page' : undefined}
                className={desktopLinkClasses('/survey')}
              >
                <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                Survey
              </Link>
            </div>
          </div>

          <div className="flex items-center">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 mr-2"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>

            {/* User menu dropdown */}
            <Menu as="div" className="relative ml-3">
              <div>
                <Menu.Button className="flex items-center max-w-xs bg-white rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 hover:bg-gray-50 transition-colors duration-200">
                  <span className="sr-only">Open user menu</span>
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary-600 to-primary-500 flex items-center justify-center shadow-sm">
                    <span className="text-white font-medium text-sm">
                      {user?.username?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="ml-2 text-gray-700 text-sm font-medium hidden lg:block">
                    {user?.username}
                  </span>
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <Menu.Item>
                    {({ active }) => (
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.username}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                          user?.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {user?.role}
                        </span>
                      </div>
                    )}
                  </Menu.Item>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } flex items-center px-4 py-2 text-sm text-gray-700 hover:text-primary-600 transition-colors duration-150`}
                      >
                        <UserIcon className="h-4 w-4 mr-3 text-gray-500" />
                        Your Profile
                      </Link>
                    )}
                  </Menu.Item>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 transition-colors duration-150 border-t border-gray-100 mt-1`}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3 text-red-500" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <Transition
        show={isMobileMenuOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 pt-2 pb-3 space-y-1">
            {/* Mobile navigation links */}
            <Link
              to="/chat"
              aria-current={isActive('/chat') ? 'page' : undefined}
              className={mobileLinkClasses('/chat')}
              onClick={closeMobileMenu}
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3 text-gray-500" />
              Chat
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                aria-current={isActive('/admin') ? 'page' : undefined}
                className={mobileLinkClasses('/admin')}
                onClick={closeMobileMenu}
              >
                <ShieldCheckIcon className="h-5 w-5 mr-3 text-gray-500" />
                Admin Dashboard
              </Link>
            )}

            <Link
              to="/experimental"
              aria-current={isActive('/experimental') ? 'page' : undefined}
              className={mobileLinkClasses('/experimental')}
              onClick={closeMobileMenu}
            >
              <BeakerIcon className="h-5 w-5 mr-3 text-gray-500" />
              Clinician Worklist
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 uppercase tracking-wide">Beta</span>
            </Link>

            <Link
              to="/survey"
              aria-current={isActive('/survey') ? 'page' : undefined}
              className={mobileLinkClasses('/survey')}
              onClick={closeMobileMenu}
            >
              <ClipboardDocumentListIcon className="h-5 w-5 mr-3 text-gray-500" />
              Survey
            </Link>

            <div className="border-t border-gray-200 my-2"></div>
            
            {/* Mobile user info */}
            <div className="px-3 py-3">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-600 to-primary-500 flex items-center justify-center shadow-sm">
                  <span className="text-white font-medium text-lg">
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.username}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                    user?.role === 'admin' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {user?.role}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Mobile profile links */}
            <Link
              to="/profile"
              className="flex items-center px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-150"
              onClick={closeMobileMenu}
            >
              <UserIcon className="h-5 w-5 mr-3 text-gray-500" />
              Your Profile
            </Link>
            
            <button
              onClick={() => {
                handleLogout();
                closeMobileMenu();
              }}
              className="flex items-center w-full text-left px-3 py-3 rounded-md text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-150"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-red-500" />
              Sign out
            </button>
          </div>
        </div>
      </Transition>
    </nav>
  );
};

export default Navbar;
